import React, { useEffect, useRef } from "react"
import { View, Text, StyleSheet, Animated, Easing } from "react-native"
import FontAwesome5 from "react-native-vector-icons/FontAwesome5"
import { theme } from "@common/theme/theme"

/**
 * Le rappel de ce qui manque, posé au-dessus du contrôle à corriger, quand le
 * parent a voulu avancer et n'a pas pu.
 *
 * Il ne se contente pas d'être là : à chaque tentative (`nudge` change), il
 * tremble et se ré-allume. Sans ça, un parent déjà positionné sur la section
 * qui re-tape le bouton ne voyait rien bouger — l'écran avait l'air en panne.
 * Rouge, pas orange : c'est un refus, pas un conseil.
 */
const FunnelToFix = ({ message, nudge = 0 }) => {
    const shake = useRef(new Animated.Value(0)).current
    const flash = useRef(new Animated.Value(0)).current

    useEffect(() => {
        shake.setValue(0)
        flash.setValue(1)
        Animated.parallel([
            Animated.timing(shake, {
                toValue: 1,
                duration: 460,
                easing: Easing.linear,
                useNativeDriver: true,
            }),
            Animated.timing(flash, {
                toValue: 0,
                duration: 900,
                easing: Easing.out(Easing.quad),
                useNativeDriver: true,
            }),
        ]).start()
    }, [nudge, shake, flash])

    return (
        <Animated.View
            accessibilityRole="alert"
            style={[
                styles.box,
                {
                    transform: [
                        {
                            translateX: shake.interpolate({
                                inputRange: [0, 0.2, 0.4, 0.6, 0.8, 1],
                                outputRange: [0, -7, 7, -5, 5, 0],
                            }),
                        },
                        {
                            scale: flash.interpolate({
                                inputRange: [0, 1],
                                outputRange: [1, 1.03],
                            }),
                        },
                    ],
                },
            ]}
        >
            <Animated.View
                pointerEvents="none"
                style={[styles.glow, { opacity: flash }]}
            />
            <FontAwesome5
                name="exclamation-circle"
                size={13}
                color={theme.colors.error}
            />
            <Text style={styles.label}>{message}</Text>
        </Animated.View>
    )
}

const styles = StyleSheet.create({
    box: {
        flexDirection: "row",
        alignItems: "flex-start",
        gap: 8,
        marginBottom: 12,
        paddingVertical: 9,
        paddingHorizontal: 11,
        borderRadius: 10,
        borderWidth: 1.5,
        borderColor: theme.colors.errorLighter,
        backgroundColor: theme.colors.errorLight,
        overflow: "hidden",
    },
    // L'éclat qui s'éteint à chaque tentative : un voile plus soutenu de la
    // même couleur, fondu sur ~1 s.
    glow: {
        ...StyleSheet.absoluteFillObject,
        backgroundColor: "rgba(234,84,85,0.22)",
    },
    label: {
        flex: 1,
        fontFamily: "Montserrat-SemiBold",
        fontSize: 12.5,
        lineHeight: 17,
        color: theme.colors.errorDark,
    },
})

export default FunnelToFix
